import { useState } from "react";
import { Copy, Check, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { fmtDate } from "./accountShared";

/** Confirmation for sensitive actions (suspend / disable / archive / role change / force logout / reset). */
export const ConfirmAction = ({ action, onCancel, onConfirm, busy }) => {
  const [note, setNote] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  if (!action) return null;
  const needsEmail = action.kind === "role";
  const ok = !needsEmail || confirmEmail.trim().toLowerCase() === (action.user.email || "").toLowerCase();
  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="account-confirm-dialog" className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="account-confirm-title">{action.title}</AlertDialogTitle>
          <AlertDialogDescription data-testid="account-confirm-description">{action.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <p className="text-sm"><span className="text-muted-foreground">Account:</span> <span className="font-medium" data-testid="account-confirm-target">{action.user.name || "—"} · {action.user.email}</span></p>
          {action.withNote && <div className="space-y-1"><Label htmlFor="confirm-note" className="text-xs">Note (audited)</Label><Textarea id="confirm-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} data-testid="account-confirm-note" /></div>}
          {needsEmail && <div className="space-y-1"><Label htmlFor="confirm-email" className="text-xs">Type the account email to confirm the role change</Label><Input id="confirm-email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={action.user.email} data-testid="account-confirm-email" /></div>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="account-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!ok || busy} onClick={(e) => { e.preventDefault(); onConfirm({ note, confirm_email: confirmEmail.trim().toLowerCase() }); }} className={action.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid="account-confirm-submit">{busy ? "Working…" : action.cta}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/** One-time reset / invite link — the only secret ever shown, never a password. */
export const ResetLinkDialog = ({ reset, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!reset) return null;
  const link = reset.url || `${window.location.origin}${reset.path}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-link-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" strokeWidth={1.5} />{reset.kind === "invite" ? "Account invitation link" : "Password reset link"}</DialogTitle>
          <DialogDescription>Share this one-time link with <span className="font-medium">{reset.email}</span> through a trusted channel. It expires {fmtDate(reset.expires_at)} and is shown only once — no password is generated or stored.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input readOnly value={link} className="font-mono-tech text-xs" data-testid="reset-link-input" onFocus={(e) => e.target.select()} />
          <Button type="button" variant="outline" onClick={copy} data-testid="reset-link-copy-btn">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
        </div>
        <Button type="button" onClick={onClose} className="w-full" data-testid="reset-link-done-btn">Done</Button>
      </DialogContent>
    </Dialog>
  );
};
